/**
 * Shared Sentry init config for all ladder-web apps.
 *
 * Apps spread `buildSentryInit(...)` into Sentry.init and call
 * `applySentryScope(...)` after, so log filtering by `app` works.
 */

import * as Sentry from "@sentry/nextjs";

export type LadderApp = "web" | "pulse" | "apollo" | "nutrition";
export type SentryRuntime = "server" | "client" | "edge";

export interface BuildSentryInitOptions {
  dsn: string;
  app: LadderApp;
  runtime: SentryRuntime;
}

export function buildSentryInit({ dsn, app, runtime }: BuildSentryInitOptions) {
  // Vercel auto-injects both VERCEL_ENV (server) and NEXT_PUBLIC_VERCEL_ENV
  // (client, inlined at build time by Next.js). On the server both resolve;
  // on the client only the NEXT_PUBLIC_ variant survives, so the fallback
  // picks the available one without needing a runtime check.
  // `||` rather than `??` throughout: an env var set to the empty string is
  // absent for our purposes, and `??` would pass "" along as a real value —
  // yielding `environment: ""` and an unlabelled event.
  const vercelEnv =
    process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.VERCEL_ENV;
  // Anything not running on Vercel is "local" — never NODE_ENV. A local
  // production build (`next build && next start`) sets NODE_ENV=production
  // with no VERCEL_ENV, so falling back to NODE_ENV labelled laptop events
  // as `environment: production`, indistinguishable from real incidents in
  // the issue stream. That is how a local run once filed itself against
  // www.joinladder.com. `next dev` was never affected, which is why this
  // hid for so long — but a production build is the only way to exercise
  // the server SDK, so it is exactly the mode used for Sentry debugging.
  const environment = vercelEnv || "local";
  const isProduction = environment === "production";

  // Local-testing escape hatches, both default-off so prod/preview/normal
  // dev are unchanged. Set in an app's `.env.local` to exercise Sentry from
  // `next dev`: SENTRY_FORCE_ENABLE=1 turns reporting on; SENTRY_DEBUG=1
  // makes the SDK log every capture/send/drop to the console — the only way
  // to actually see whether a span reached Sentry from a local run.
  const forceEnable = process.env.SENTRY_FORCE_ENABLE === "1";
  const debugMode = process.env.SENTRY_DEBUG === "1";

  return {
    dsn,
    environment,
    // Only report from production Vercel deploys. Preview deploys (PR
    // builds) and local `next dev` send nothing — they're short-lived,
    // we debug them with browser devtools, and routing their events into
    // the prod project drowns out real signal. NODE_ENV is "production"
    // on ALL Vercel builds (preview + prod), so we have to gate on
    // VERCEL_ENV to single out canonical prod. `SENTRY_FORCE_ENABLE=1`
    // opts a local dev server in for deliberate Sentry testing.
    enabled: vercelEnv === "production" || forceEnable,
    // Lower in prod to control span volume; full fidelity everywhere else.
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    // Sentry Logs (v10+). Sends Sentry.logger.* calls as structured logs.
    enableLogs: true,
    // Required for setUser email/ip to flow through to events. Paired with
    // scrubServerRequestPii() below — sendDefaultPii would otherwise attach
    // Cookie + Authorization headers, both of which carry the Rocky session.
    sendDefaultPii: true,
    debug: debugMode,
    // The Node SDK defaults serverName to os.hostname(). On Vercel that
    // usefully identifies the instance; on a laptop it is a developer's
    // personal machine name, which has already reached prod Sentry once
    // alongside a home directory path and geo. Pin it off-Vercel. The
    // browser SDK ignores this option, so gating on vercelEnv alone is
    // enough — no runtime check needed.
    ...(vercelEnv ? {} : { serverName: `local-${app}` }),
    initialScope: {
      tags: { app, runtime },
    },
    tracePropagationTargets: [
      /^https:\/\/rocky-api\.(dev|prod)\.ltdev\.io/,
      /^https:\/\/(?:.*\.)?joinladder\.com/,
      /^\//, // same-origin relative requests
    ],
  };
}

// Accept Sentry.Event (the union of ErrorEvent | TransactionEvent | etc.)
// because addEventProcessor runs on every event type, not just errors.
// Typing as ErrorEvent was incorrect — transactions also pass through here.
function scrubServerRequestPii(event: Sentry.Event): Sentry.Event {
  if (event.request) {
    if (event.request.cookies) {
      delete event.request.cookies;
    }
    if (event.request.headers && typeof event.request.headers === "object") {
      const h = event.request.headers as Record<string, unknown>;
      delete h.cookie;
      delete h.Cookie;
      delete h.authorization;
      delete h.Authorization;
    }
  }
  return event;
}

/**
 * Call after `Sentry.init` in each runtime. Sentry Logs reads from global-
 * scope *attributes*, not init `tags`, so we set both: tags for event/trace
 * filtering, attributes for log filtering.
 *
 * Also installs a server/edge event processor that strips the Cookie and
 * Authorization headers — sendDefaultPii would otherwise auto-attach them
 * and both carry the Rocky session token.
 */
export function applySentryScope({
  app,
  runtime,
}: Pick<BuildSentryInitOptions, "app" | "runtime">) {
  const scope = Sentry.getGlobalScope();
  scope.setTags({ app, runtime });
  scope.setAttributes({ app, runtime });

  if (runtime !== "client") {
    scope.addEventProcessor(scrubServerRequestPii);
  }
}

export interface SentryUserInfo {
  id: string;
  email?: string;
  username?: string;
}

/**
 * Set or clear the Sentry user on the current scope. Client-side mounts a
 * `<SentryUserSync />` style component that calls this when the user resolves.
 * Server-side, call from auth middleware or route handlers so the per-request
 * isolation scope carries the user.
 *
 * Pass only the fields you want visible in events — never spread the full
 * user object, which can include session tokens or other secrets.
 */
export function syncSentryUser(user: SentryUserInfo | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
}
